import type { Accessor, JSX, VoidComponent } from "solid-js";
import { createMemo, splitProps } from "solid-js";
import * as outline from "solid-heroicons/outline";
import * as solid from "solid-heroicons/solid";
import * as custom from "./CustomIcons";

type HeroIconName = keyof typeof outline;
type CustomIconName = keyof typeof custom;
export type IconName = HeroIconName | CustomIconName;

type IconSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
export type IconType = "outline" | "solid";
export type IconProps = {
  name: IconName;
  type: IconType;
  size: IconSize;
} & Omit<JSX.SvgSVGAttributes<SVGSVGElement>, "children">;

const solidIcon = solid;
const outlineIcon = outline;

export const Icon: VoidComponent<IconProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["name", "type", "size", "class", "classList"]);

  const path: Accessor<{ path: () => JSX.Element; outline: boolean; isCustom: boolean }> = createMemo(() => {
    const isCustom = localProps.name in custom;
    const element = isCustom
      ? custom[localProps.name as CustomIconName]
      : localProps.type === "solid"
        ? solidIcon[localProps.name as HeroIconName]
        : outlineIcon[localProps.name as HeroIconName];

    return { ...element, isCustom };
  });

  return (
    <svg
      viewBox={path().outline || path().isCustom ? "0 0 24 24" : "0 0 20 20"}
      style={{
        fill: path().outline ? "none" : "currentColor",
        stroke: path().outline ? "currentColor" : "none",
      }}
      aria-hidden="true"
      class={localProps.class}
      classList={{
        ...localProps.classList,
        "size-3": localProps.size === "xs",
        "size-3.5": localProps.size === "sm",
        "size-4": localProps.size === "md",
        "size-5": localProps.size === "lg",
        "size-6": localProps.size === "xl",
        "size-9": localProps.size === "2xl",
        "size-13": localProps.size === "3xl",
        "stroke-2": localProps.type === "outline",
      }}
      {...otherProps}
    >
      {path().path()}
    </svg>
  );
};
